import { ApolloError } from '@apollo/client';
import { convertToGraphQLErrorsExtendedFormat } from 'services/error-message-service';

export const executeSigningWithRetry = async <T extends { success?: boolean }>({
  signingFn,
  onSuccess,
  onError,
  handleGraphQLErrors,
}: {
  signingFn: () => Promise<{ data?: T | null; error?: Error }>;
  onSuccess: () => void;
  onError?: (error?: Error) => void;
  handleGraphQLErrors?: (errors: any) => void;
}): Promise<void> => {
  try {
    const { data, error } = await signingFn();
    if (data?.success) {
      onSuccess();
    } else {
      onError?.(error as Error);
    }
  } catch (error) {
    // If it's an ApolloError with GraphQL errors, handle them manually
    if (error instanceof ApolloError && error.graphQLErrors?.length) {
      // Manually convert and handle the GraphQL errors
      if (handleGraphQLErrors) {
        const extendedErrors = convertToGraphQLErrorsExtendedFormat(
          error.graphQLErrors,
        );
        handleGraphQLErrors(extendedErrors);
      }

      onError?.(error as Error);
      return; // Don't re-throw, we've handled it
    }

    // Only retry on network failures (like "Failed to fetch")
    if ((error as Error)?.message.includes('Failed to fetch')) {
      try {
        const { data } = await signingFn();
        if (data?.success) {
          onSuccess();
        } else {
          onError?.(error as Error);
        }
      } catch (retryError) {
        // If retry also fails with GraphQL error, handle it manually
        if (
          retryError instanceof ApolloError &&
          retryError.graphQLErrors?.length &&
          handleGraphQLErrors
        ) {
          const extendedErrors = convertToGraphQLErrorsExtendedFormat(
            retryError.graphQLErrors,
          );
          handleGraphQLErrors(extendedErrors);
          onError?.(retryError as Error);
          return;
        }
        onError?.(error as Error);
      }
    } else {
      onError?.(error as Error);
    }
  }
};
